import { useEffect, useMemo, useState } from 'react'
import { t } from 'i18next'
import FileBlock from '../FileBlock'
import { Popover, MenuList } from 'vnetwork-ui'
import { useQueryClient } from '@tanstack/react-query'
import {
    FetchThumbnail,
    PresignedGetUrl,
    DuplicateObject,
    DeleteFile,
    CreateShareLink,
    GetObjectPermissionLevel,
    CheckModifyItem,
} from '@/modules/Drive/services'
import { AddObjectToRecent } from '@/modules/Drive/services'
import {
    IMAGE_EXTENSIONS,
    ObjectType,
    VIDEO_EXTENSIONS,
} from '@/modules/Drive/types'
import {
    extractExtension,
    FileIcon,
    extractUuidFromObjectKey,
} from '@/modules/Drive/utils'
import { downloadThumbnailFromPresignedUrl } from '@/modules/Drive/utils/ThumbnailGenerator'
import useUploadThumbnails from '@/hooks/useUploadThumbnails'
import FolderIcons from '@/icons/FolderIcons/FolderIcons'
import ImageIcons from '@/icons/ImageIcons'
import { getFileName, extractObjectName } from '../../helpers/helper'
import {
    PermissionLevel,
    checkPermission,
} from '@/modules/Drive/utils/DrivePermission/permission'
import { limitHeic, limitThumb } from '@/modules/Drive/services/previewLimiter'
import datacentral from '@/utils/axios/datacentral'
import DownLoadIcons from '@/icons/DownLoadIcons'
import EditIcons from '@/icons/EditIcons'
import MakeCopyIcons from '@/icons/MakeCopyIcons'
import ShareFileIcons from '@/icons/ShareFileIcons'
import StarIcons from '@/icons/StarIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import LinkIcons from '@/icons/LinkIcons'
import MoveIcons from '@/icons/MoveIcons'
import toast from 'react-hot-toast'
import { useFilePreview } from '@/contexts/file-preview'
import useAuthStore from '@/stores/useAuthStore'
import { useModal } from '@/contexts/modal'
import { useConfirmation } from '@/contexts/confirmation'
import { ModifyObjectProps } from '@/modals/DriveModals/ModifyObjectModal'
import { MoveObjectProps } from '@/modals/DriveModals/MoveObjectModal'
import { OFFICE_EXTENSIONS } from '@/hooks/useFilePreviewWithPresigned'
import FileRow from '../FileRow'
import useDriveStore, { Sections } from '@/stores/useDriveStore'
import { useNavigate } from 'react-router-dom'
import { ShareObjectModalProps } from '@/modals/DriveModals/ShareObjectModal'
import { ObjectInfoModalProps } from '@/modals/DriveModals/ObjectInfoModal'
import MenuListSkeleton from '@/components/skeletons/MenuList'
import axios from 'axios'
import { ErrorResponse } from '@/types/shared'
import {
    useAddStarredMutation,
    useRemoveStarredMutation,
} from '@/modules/Drive/services/StarredObject'
import { CreateStarredObjectParams } from '@/modules/Drive/types'
import InfoIcons from '@/icons/InfoIcons'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

type FileItemProps = {
    objectKey: string
    type: ObjectType
    folderName?: string
    size?: number
    lastModified?: string
    totalFiles?: number
    downloadHandler?: () => void
    onMultiplePreview?: () => void
}
const hasThumbnailExtensions = [
    ...IMAGE_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    'docx',
    'doc',
    'xlsx',
    'pptx',
    'pdf',
]

const customMenuItemStyle = 'hover:bg-secondary-20 text-sm gap-3'

const disabledMenuStyle =
    customMenuItemStyle + ' pointer-events-none opacity-50'

const FileItem = ({
    objectKey,
    type,
    folderName,
    downloadHandler,
    size,
    lastModified,
    onMultiplePreview,
    totalFiles,
}: FileItemProps) => {
    const [thumbnail, setThumbnail] = useState<string | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [itemPermission, setItemPermission] = useState<
        PermissionLevel | undefined
    >(undefined)
    const [heicPreviewUrl, setHeicPreviewUrl] = useState<string | null>(null)
    const [shouldFetchHeic, setShouldFetchHeic] = useState(false)
    const viewType = useDriveStore((state) => state.viewType)
    const starredObjectKeys = useDriveStore((state) => state.starredObjectKeys)
    const setStarredObjectKeys = useDriveStore(
        (state) => state.setStarredObjectKeys
    )
    const itemNames = useDriveStore((state) => state.itemNames)
    const { employee } = useAuthStore()
    const { preview } = useFilePreview()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const isSelectedMode = useDriveStore((state) => state.isSelectedMode)
    const selectedItems = useDriveStore((state) => state.selectedItems)
    const isSelectedAll = useDriveStore((state) => state.isSelectedAll)
    const appendSelectedItems = useDriveStore(
        (state) => state.appendSelectedItems
    )
    const removeSelectedItems = useDriveStore(
        (state) => state.removeSelectedItems
    )

    // Check if file is HEIC
    const isHeicFile = extractExtension(objectKey).toLowerCase() === 'heic'

    useEffect(() => {
        if (isSelectedAll) {
            appendSelectedItems(objectKey)
        } else {
            removeSelectedItems(objectKey)
        }
    }, [isSelectedAll])

    // Cleanup HEIC preview URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (heicPreviewUrl) {
                URL.revokeObjectURL(heicPreviewUrl)
            }
        }
    }, [heicPreviewUrl])

    const isStarred = useMemo(
        () => starredObjectKeys?.includes(objectKey),
        [starredObjectKeys, objectKey]
    )
    const fileName = useMemo(() => {
        if (type === ObjectType.FOLDER) {
            if (objectKey.split('/').filter(Boolean)?.length === 3) {
                if (folderName?.length != 0) {
                    return folderName
                }
                if (itemNames[extractUuidFromObjectKey(objectKey)]) {
                    return itemNames[extractUuidFromObjectKey(objectKey)]
                }
            }
            return extractObjectName(objectKey)
        }
        return getFileName(objectKey)
    }, [type, folderName, itemNames, objectKey])

    const ownerUuid = useMemo(
        () => extractUuidFromObjectKey(objectKey),
        [objectKey]
    )

    const { uploadAsync: uploadThumbnails } = useUploadThumbnails({
        prefix: 'thumbnails',
        onError: (error) => {
            console.error('Upload failed:', error)
        },
    })

    const { data: thumbnailData, isError } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.THUMBNAIL, objectKey],
        queryFn: () => limitThumb(() => FetchThumbnail(objectKey)),
        enabled:
            type &&
            type === ObjectType.FILE &&
            hasThumbnailExtensions.some((ext) =>
                extractExtension(objectKey)
                    .toLowerCase()
                    .includes(ext.toLowerCase())
            ),
    })

    // React Query hook for fetching HEIC preview
    const {
        data: heicPreviewBlob,
        isLoading: isHeicLoading,
        isFetching: isHeicFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.HEIC_PREVIEW, objectKey],
            queryFn: async ({ signal }) =>
                limitHeic(async () => {
                    const response = await datacentral.get(
                        '/drive/heic-preview',
                        {
                            params: { path: objectKey },
                            responseType: 'blob',
                            signal,
                        }
                    )
                    return response.data as Blob
                }),
            enabled: !!isHeicFile && shouldFetchHeic,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
        { showLoading: false, showError: false }
    )

    // Permission fetching mutation
    const {
        mutateAsync: getObjectPermissionLevel,
        isPending: isGettingPermission,
    } = useMutationWithHandler(
        {
            mutationFn: () =>
                GetObjectPermissionLevel(objectKey, employee?.employee_id),
        },
        {
            showSuccessToast: false,
            showErrorToast: false,
            onSuccess: (data) => {
                const lvl = (data as any)?.result?.share_permission_level
                if (
                    lvl === PermissionLevel.READ ||
                    lvl === PermissionLevel.VIEW ||
                    lvl === PermissionLevel.EDIT
                ) {
                    setItemPermission(lvl)
                }
            },
            onError: (error: any) => {
                const errorCode = String(error?.response?.data?.error)
                if (errorCode === '500' || errorCode === '403') {
                    setItemPermission(PermissionLevel.VIEW)
                }
            },
            reactQueryKeys: [],
        }
    )

    const { mutateAsync: removeStarredMutation } = useRemoveStarredMutation(
        employee?.employee_uuid,
        setStarredObjectKeys
    )
    const { mutateAsync: addStarredMutation } = useAddStarredMutation(
        employee?.employee_uuid,
        setStarredObjectKeys
    )

    // Start fetching HEIC preview after thumbnail is done or not applicable
    useEffect(() => {
        if (isHeicFile) {
            // For HEIC files that also need thumbnails
            if (
                hasThumbnailExtensions.some((ext) =>
                    extractExtension(objectKey)
                        .toLowerCase()
                        .includes(ext.toLowerCase())
                )
            ) {
                // Wait for thumbnail to finish (success or error)
                if (thumbnailData !== undefined || isError) {
                    setShouldFetchHeic(true)
                }
            } else {
                // For HEIC files that don't need thumbnails, start immediately
                setShouldFetchHeic(true)
            }
        }
    }, [isHeicFile, thumbnailData, isError, objectKey])

    // Permission fetching logic
    const fetchPermissions = async () => {
        // if (isAdmin) {
        //     setItemPermission(PermissionLevel.EDIT)
        //     return
        // }
        // If the file is owned by the current user, set the permission to edit
        if (ownerUuid === employee?.employee_uuid) {
            setItemPermission(PermissionLevel.EDIT)
            return
        }

        // Change the permission of the file base on the result of the api
        await getObjectPermissionLevel()
    }

    useEffect(() => {
        const handleThumbnail = async () => {
            if (isError || !thumbnailData) {
                setThumbnail(null)
                return
            }

            const result = thumbnailData.result

            // Case 1: Thumbnail exists and is available
            if (result.thumbnail && result.available) {
                setThumbnail(result.thumbnail)
                return
            }

            setThumbnail(null)

            // Case 2: Thumbnail key exists but the object is not available (needs regeneration)
            if (result.thumbnail && !result.available && !isHeicFile) {
                try {
                    const response = await PresignedGetUrl(objectKey)
                    const presignedUrl = decodeURIComponent(response.result)

                    const thumbFile = await downloadThumbnailFromPresignedUrl(
                        presignedUrl,
                        result.thumbnail
                    )

                    await uploadThumbnails({
                        files: [thumbFile],
                        isThumbnail: true,
                    })
                    queryClient.invalidateQueries({
                        queryKey: ['thumbnail', objectKey],
                    })
                } catch (err) {
                    console.error('Thumbnail regeneration failed:', err)
                }
            }
        }

        handleThumbnail()
    }, [thumbnailData, isError, objectKey])

    const handleDownload = async () => {
        if (downloadHandler) {
            downloadHandler()
            return
        }
        toast.error(t('drive.download-failed'))
    }

    const handleDuplicate = async () => {
        try {
            await DuplicateObject(objectKey)
            toast.success(t('drive.duplicate-success'))
            const pathname = window.location.pathname
            const queryKey =
                pathname.split('/').filter(Boolean).length > 2
                    ? 'folder-explorer'
                    : Sections.MyDrive
            queryClient.invalidateQueries({ queryKey: [queryKey] })
        } catch (error: any) {
            if (axios.isAxiosError<ErrorResponse>(error)) {
                toast.error(
                    error.response?.data.error_description || t('failed-copy')
                )
            } else {
                console.error(error)
                toast.error(t('something-went-wrong'))
            }
        }
    }

    const handleShareLink = async () => {
        try {
            const { result } = await CreateShareLink({
                share_object_key: objectKey,
                share_object_type: type,
                share_is_public: true,
                share_permission_level: PermissionLevel.READ,
                share_link_created_by: employee?.employee_id ?? 0,
            })
            const url = `${window.location.origin}/drive/shared?token=${result}`
            await navigator.clipboard.writeText(url)
            toast.success(t('drive.copy-link-success'))
        } catch (error: any) {
            if (error.response?.status === 403) {
                toast.error(t('drive.no-share-permission'))
            } else {
                toast.error(
                    error.response?.data.error_description || t('failed-copy')
                )
            }
        }
    }

    const handleDelete = async () => {
        try {
            await DeleteFile(objectKey)
            toast.success(t('drive.delete-success'))
            const pathname = window.location.pathname
            const queryKey =
                pathname.split('/').filter(Boolean).length > 2
                    ? 'folder-explorer'
                    : Sections.MyDrive
            queryClient.invalidateQueries({ queryKey: [queryKey] })
        } catch (error) {
            console.error(error)
            toast.error(t('drive.delete-failed'))
        }
    }

    const handlePreview = async () => {
        if (onMultiplePreview && totalFiles && totalFiles > 1) {
            onMultiplePreview()
            return
        }

        // Handle HEIC files with loading state
        if (isHeicFile) {
            const loadingToast = toast.loading(t('Converting HEIC image...'))

            try {
                // Get presigned URL first (usually faster)
                const presignedPromise = PresignedGetUrl(objectKey)

                // Check if HEIC preview is already cached
                if (heicPreviewBlob && !isHeicLoading && !isHeicFetching) {
                    // Preview is ready, use it immediately
                    const presignedResponse = await presignedPromise
                    const downloadUrl = presignedResponse.result

                    // Clean up previous URL if exists
                    if (heicPreviewUrl) {
                        URL.revokeObjectURL(heicPreviewUrl)
                    }

                    // Create object URL from cached blob
                    const previewUrl = URL.createObjectURL(heicPreviewBlob)
                    setHeicPreviewUrl(previewUrl)

                    // Show preview immediately with cached data
                    preview(
                        previewUrl,
                        downloadUrl,
                        {
                            attachment_file_name: fileName || '',
                            attachment_file_url: previewUrl, // Converted JPEG for viewing
                            attachment_file_type: 'image/jpeg',
                        },
                        true
                    )
                    toast.dismiss(loadingToast)
                } else {
                    // Wait for both the preview blob and presigned URL
                    const checkInterval = setInterval(() => {
                        // Check if the query has completed
                        queryClient.getQueryState([
                            QUERY_KEYS.HEIC_PREVIEW,
                            objectKey,
                        ])
                    }, 100)

                    // Create a promise that resolves when heicPreviewBlob is ready
                    const waitForBlob = new Promise<Blob>((resolve, reject) => {
                        const maxWaitTime = 30000 // 30 seconds timeout
                        const startTime = Date.now()

                        const checkBlob = setInterval(() => {
                            const queryState = queryClient.getQueryState([
                                QUERY_KEYS.HEIC_PREVIEW,
                                objectKey,
                            ])

                            if (queryState?.data) {
                                clearInterval(checkBlob)
                                clearInterval(checkInterval)
                                resolve(queryState.data as Blob)
                            } else if (queryState?.status === 'error') {
                                clearInterval(checkBlob)
                                clearInterval(checkInterval)
                                reject(new Error('Failed to load HEIC preview'))
                            } else if (Date.now() - startTime > maxWaitTime) {
                                clearInterval(checkBlob)
                                clearInterval(checkInterval)
                                reject(
                                    new Error('Timeout loading HEIC preview')
                                )
                            }
                        }, 100)
                    })

                    // Wait for both promises
                    const [presignedResponse, previewBlob] = await Promise.all([
                        presignedPromise,
                        waitForBlob,
                    ])

                    const downloadUrl = presignedResponse.result

                    // Dismiss loading toast
                    toast.dismiss(loadingToast)

                    // Clean up previous URL if exists
                    if (heicPreviewUrl) {
                        URL.revokeObjectURL(heicPreviewUrl)
                    }

                    // Create object URL from blob
                    const previewUrl = URL.createObjectURL(previewBlob)
                    setHeicPreviewUrl(previewUrl)

                    // Update preview with actual URLs
                    preview(
                        previewUrl,
                        downloadUrl,
                        {
                            attachment_file_name: fileName || '',
                            attachment_file_url: previewUrl, // Converted JPEG for viewing
                            attachment_file_type: 'image/jpeg',
                        },
                        true
                    )
                }
            } catch (error) {
                // Dismiss loading toast if it exists
                if (loadingToast) {
                    toast.dismiss(loadingToast)
                }
                console.error('Failed to preview HEIC file:', error)
                toast.error(t('Failed to preview HEIC file'))
            }
            return
        }

        // Handle other file types
        try {
            const response = await PresignedGetUrl(objectKey)
            let url = response.result
            if (
                extractExtension(objectKey) &&
                OFFICE_EXTENSIONS.some((ext) =>
                    extractExtension(objectKey).includes(ext)
                )
            ) {
                url = encodeURIComponent(url)
            }
            preview(url, url, {
                attachment_file_name: fileName || '',
                attachment_file_url: url,
            })
        } catch (error) {
            console.error(error)
            toast.error(t('try-again'))
        }
    }

    const handleUpdateRecent = async () => {
        if (!employee) return
        try {
            const ownerUuid = extractUuidFromObjectKey(objectKey)
            await AddObjectToRecent({
                recent_object_key: objectKey,
                recent_object_name: fileName || '',
                recent_object_size: size || 0,
                recent_object_last_modified:
                    lastModified || new Date().toISOString(),
                employee_uuid: employee.employee_uuid,
                employee_uuid_2: ownerUuid,
            })
            queryClient.invalidateQueries({ queryKey: ['recentList'] })
        } catch (error) {
            // non-blocking
            console.error('Failed to update recent list', error)
        }
    }

    const handleMarkFile = async () => {
        if (!employee) return
        try {
            if (isStarred) {
                await removeStarredMutation({
                    uuid: employee.employee_uuid,
                    objectName: objectKey,
                })
            } else {
                const payload: CreateStarredObjectParams = {
                    starred_object_key: objectKey,
                    starred_object_name: fileName || '',
                    starred_object_type:
                        type === ObjectType.FOLDER
                            ? ObjectType.FOLDER
                            : ObjectType.FILE,
                    employee_uuid: employee?.employee_uuid,
                    starred_object_last_modified: lastModified || null,
                    starred_object_size: size || 0,
                    employee_uuid_2: ownerUuid,
                }
                await addStarredMutation(payload)
            }
        } catch (error: any) {
            toast.error(error?.message || t('something-went-wrong'))
        }
    }

    const handleInfo = () => {
        setModal<ObjectInfoModalProps>('DriveModals/ObjectInfoModal/index', {
            objectKey: objectKey,
            objectName: fileName || '',
            objectType: type,
            size: size,
            lastModified: lastModified,
        })
    }

    const handleClick = async (e: {
        type: string
        preventDefault: () => void
    }) => {
        e.preventDefault()
        if (isSelectedMode) {
            if (selectedItems.includes(objectKey)) {
                removeSelectedItems(objectKey)
            } else {
                appendSelectedItems(objectKey)
            }
        } else if (e.type === 'click') {
            // Handle navigate folders
            if (type == ObjectType.FOLDER) {
                // Remove 'pub/drives/' prefix from objectKey if present before navigating
                const cleanedObjectKey = objectKey.startsWith('pub/drives/')
                    ? objectKey.replace(/^pub\/drives\//, '')
                    : objectKey
                navigate(`/drive/folders/${cleanedObjectKey}`)
            } else {
                handlePreview()
                handleUpdateRecent()
            }
        } else if (e.type === 'contextmenu') {
            setIsOpen(true)
            // Fetch permissions when user clicks
            if (!itemPermission) {
                await fetchPermissions()
            }
        }
    }

    const iconNode = useMemo(
        () =>
            type === ObjectType.FOLDER ? (
                <FolderIcons />
            ) : FileIcon[extractExtension(objectKey).toLowerCase()] ? (
                FileIcon[extractExtension(objectKey).toLowerCase()]
            ) : (
                <ImageIcons className="text-red-500" />
            ),
        [type, objectKey]
    )

    return (
        <Popover
            visible={isOpen}
            onClickOutside={() => setIsOpen(false)}
            placement="bottom-end"
            appendTo={document.body}
            interactive
            render={() =>
                !itemPermission || isGettingPermission ? (
                    <MenuListSkeleton numberOfItems={7} />
                ) : (
                    <MenuList
                        size="medium"
                        onSelect={() => setIsOpen(false)}
                        menuitems={[
                            {
                                children: t('download-file'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(
                                            itemPermission,
                                            'download'
                                        )
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: (
                                    <DownLoadIcons className="text-[#626262]" />
                                ),
                                onClick: () => {
                                    setIsOpen(false)
                                    handleDownload()
                                },
                            },
                            {
                                children: t('rename'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'modify')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: <EditIcons className="text-[#626262]" />,
                                onClick: async () => {
                                    try {
                                        const isModify =
                                            await CheckModifyItem(objectKey)
                                        if (!isModify.result) {
                                            toast.error(isModify.message)
                                            return
                                        }
                                    } catch (error: any) {
                                        if (error.response?.status === 403) {
                                            toast.error(
                                                t('drive.no-modify-permission')
                                            )
                                        } else {
                                            toast.error(error.message)
                                        }
                                        return
                                    }

                                    setModal<ModifyObjectProps>(
                                        'DriveModals/ModifyObjectModal/index',
                                        {
                                            title: t('rename'),
                                            placeholder: t(
                                                'create-folder-placeholder'
                                            ),
                                            initialValue: fileName || '',
                                            source: objectKey,
                                            type: type,
                                        }
                                    )
                                    setIsOpen(false)
                                },
                                divider: true,
                            },
                            {
                                children: t('make-copy'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'modify')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: (
                                    <MakeCopyIcons className="text-[#626262]" />
                                ),
                                onClick: () => {
                                    setIsOpen(false)
                                    handleDuplicate()
                                },
                            },
                            {
                                children: t('share-file'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'share')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: (
                                    <ShareFileIcons className="text-[#626262]" />
                                ),
                                onClick: () => {
                                    setModal<ShareObjectModalProps>(
                                        'DriveModals/ShareObjectModal/index',
                                        {
                                            objectKey: objectKey,
                                            objectName: fileName || '',
                                            objectType: type,
                                        }
                                    )
                                    setIsOpen(false)
                                },
                            },
                            {
                                children: t('copy-link'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'share')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: <LinkIcons className="text-[#626262]" />,
                                onClick: () => {
                                    setIsOpen(false)
                                    handleShareLink()
                                },
                            },
                            {
                                children: isStarred
                                    ? t('unmark-file')
                                    : t('mark-file'),
                                className: customMenuItemStyle,
                                icon: <StarIcons className="text-[#626262]" />,
                                onClick: () => {
                                    handleMarkFile()
                                    setIsOpen(false)
                                },
                            },
                            {
                                children: t('move-file'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'modify')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: <MoveIcons className="text-[#626262]" />,
                                onClick: async () => {
                                    try {
                                        const isModify =
                                            await CheckModifyItem(objectKey)
                                        if (!isModify.result) {
                                            toast.error(isModify.message)
                                            return
                                        }
                                    } catch (error: any) {
                                        toast.error(error.message)
                                        return
                                    }
                                    setModal<MoveObjectProps>(
                                        'DriveModals/MoveObjectModal/index',
                                        {
                                            objectKey: objectKey,
                                            objectName: fileName || '',
                                            objectType: type,
                                        }
                                    )
                                    setIsOpen(false)
                                },
                            },
                            {
                                children: t('drive.info'),
                                className: customMenuItemStyle,
                                icon: <InfoIcons className="text-[#626262]" />,
                                onClick: () => {
                                    setIsOpen(false)
                                    handleInfo()
                                },
                                divider: true,
                            },
                            {
                                children: t('delete-file'),
                                className: !itemPermission
                                    ? customMenuItemStyle
                                    : checkPermission(itemPermission, 'delete')
                                      ? customMenuItemStyle
                                      : disabledMenuStyle,
                                icon: (
                                    <DeleteOutlineIcons className="text-[#626262]" />
                                ),
                                onClick: async () => {
                                    try {
                                        const isModify =
                                            await CheckModifyItem(objectKey)
                                        if (!isModify.result) {
                                            toast.error(isModify.message)
                                            return
                                        }
                                    } catch (error: any) {
                                        toast.error(error.message)
                                        return
                                    }
                                    setConfirmation({
                                        open: true,
                                        title: t('delete-file-confirm-title'),
                                        description: t(
                                            'delete-file-confirm-desc'
                                        ),
                                        onConfirm: handleDelete,
                                    })
                                    setIsOpen(false)
                                },
                            },
                        ]}
                    />
                )
            }
        >
            {viewType === 'grid' ? (
                <FileBlock
                    filename={fileName || ''}
                    isStarred={isStarred}
                    thumbnail={thumbnail}
                    icon={iconNode}
                    onClick={(e) => {
                        handleClick(e)
                    }}
                    isSelected={selectedItems.includes(objectKey)}
                    isSelectedMode={isSelectedMode}
                />
            ) : (
                <FileRow
                    filename={fileName || ''}
                    isStarred={isStarred}
                    icon={iconNode}
                    onClick={(e) => {
                        handleClick(e)
                    }}
                    isSelected={selectedItems.includes(objectKey)}
                    isSelectedMode={isSelectedMode}
                    size={size}
                    isFolder={type === ObjectType.FOLDER}
                    lastModified={lastModified}
                />
            )}
        </Popover>
    )
}

export default FileItem
